import { useChatRooms } from '@commenda/api';
import {
  HorizontalTabsList,
  Tabs,
  TabsContent,
  TabsTrigger,
} from '@commenda/kakashi';
import { IChatRooms } from '@commenda/types';
import { ChatRoomModel } from '@commenda/utils';
import { ChatRoomType } from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { ChatRoom } from '../ChatRoom';
import { AllMessagesView } from './AllMessagesView';
import { ClosedMessagesView } from './ClosedMessagesView';
import { UnreadMessagesView } from './UnreadMessagesView';

/**
 * Sort chat rooms so that GENERAL_THREAD rooms appear first,
 * preserving the relative order of all other rooms.
 */
function pinGeneralThreadFirst(
  rooms: IChatRooms['chatRooms'] | undefined
): IChatRooms['chatRooms'] | undefined {
  if (!rooms) return rooms;
  const general: IChatRooms['chatRooms'] = [];
  const rest: IChatRooms['chatRooms'] = [];
  for (const room of rooms) {
    if (room.type === "GENERAL_THREAD") {
      general.push(room);
    } else {
      rest.push(room);
    }
  }
  return [...general, ...rest];
}

interface AllChatRoomsProps {
  businessEntityId?: number;
  chatRoomId?: string;
  companyId: number;
  open: boolean;
  disableChatBackButton?: boolean;
  userId: string;
}

export const AllChatRooms = ({
  businessEntityId,
  companyId,
  open,
  chatRoomId,
  disableChatBackButton = false,
  userId,
}: AllChatRoomsProps) => {
  const [selectedChatRoomId, onSelectChatRoomId] = useState<string | null>(
    chatRoomId || null
  );
  const router = useRouter();

  const [currentTab, setCurrentTab] = useState<string>('unread');
  const { data: chatRoomsRaw, refetch: refetchChatRooms } = useChatRooms({
    businessEntityId,
    companyId,
    // Do not exclude empty rooms — the General thread should always appear
    // even when it has no messages yet.
    excludeEmptyChatRooms: false,
  });

  // Pin General thread to the top of the list.
  const chatRooms = useMemo(
    () =>
      chatRoomsRaw
        ? { ...chatRoomsRaw, chatRooms: pinGeneralThreadFirst(chatRoomsRaw.chatRooms) ?? [] }
        : chatRoomsRaw,
    [chatRoomsRaw]
  );

  useEffect(() => {
    if (open) {
      refetchChatRooms();
      // Only select chatRoomId if not already selected
      if (chatRoomId && selectedChatRoomId !== chatRoomId) {
        onSelectChatRoomId(chatRoomId);
      }
    }
    if (!open && selectedChatRoomId !== null) {
      onSelectChatRoomId(null);
    }
  }, [open, chatRoomId, refetchChatRooms, selectedChatRoomId]);

  const TABS_NEW = [
    { title: 'Unread', tabId: 'unread' },
    { title: 'All', tabId: 'all' },
    { title: 'Closed', tabId: 'closed' },
  ];

  if (!open) return null;

  if (selectedChatRoomId) {
    return (
      <ChatRoom
        variant="customer-only"
        open
        companyId={companyId}
        userId={userId}
        chatRoomId={selectedChatRoomId}
        onBackToChatList={
          !disableChatBackButton
            ? () => {
                onSelectChatRoomId(null);
              }
            : undefined
        }
        goToConnectedFilling={
          chatRooms?.chatRooms?.find((r) => r.id === selectedChatRoomId)
            ? (() => {
                const selected = chatRooms.chatRooms.find(
                  (r) => r.id === selectedChatRoomId
                );
                if (selected?.serviceRequest) {
                  return () =>
                    router.push(
                      `/service-request/${selected.serviceRequest.id}`
                    );
                }
                return undefined;
              })()
            : undefined
        }
      />
    );
  }

  // show the business entity name on the chat rooms list only if we do not filter by business entity
  const showBusinessEntityName = !businessEntityId;

  return (
    <div
      style={{
        boxShadow: '0px 1px 2px 0px #14120F0F, 0px 1px 3px 0px #14120F1A',
      }}
      className="flex flex-col border border-border-primary rounded-md absolute top-[8px] right-0 w-[500px] z-50 bg-white overflow-clip"
    >
      <div className="px-xl py-lg bg-bg-secondary">
        <span className="txt-small-bold text-text-tertiary">Messages</span>
      </div>
      <Tabs
        value={currentTab}
        onValueChange={setCurrentTab}
        className="flex flex-col flex-grow w-full"
      >
        <HorizontalTabsList variant="underline" className="bg-bg-secondary">
          {TABS_NEW.map((eachTab) => {
            return (
              <TabsTrigger
                key={eachTab.tabId}
                value={eachTab.tabId}
                variant="underline"
              >
                {eachTab.title}
              </TabsTrigger>
            );
          })}
        </HorizontalTabsList>

        {/* Unread Messages */}
        <TabsContent
          value="unread"
          className="h-[60vh] overflow-y-auto bg-white rounded-b-md"
        >
          <UnreadMessagesView
            showBusinessEntityName={showBusinessEntityName}
            onSelectChatRoomId={onSelectChatRoomId}
            chatRooms={chatRooms?.chatRooms.filter(
              (_chatRoom) => ChatRoomModel.getTotalUnread(_chatRoom) > 0
            )}
          />
        </TabsContent>

        <TabsContent
          value="all"
          className="h-[60vh] overflow-y-auto bg-white rounded-b-md"
        >
          <AllMessagesView
            showBusinessEntityName={showBusinessEntityName}
            chatRooms={chatRooms?.chatRooms}
            onSelectChatRoomId={onSelectChatRoomId}
          />
        </TabsContent>

        <TabsContent
          value="closed"
          className="h-[60vh] overflow-y-auto bg-white rounded-b-md"
        >
          <ClosedMessagesView
            showBusinessEntityName={showBusinessEntityName}
            chatRooms={chatRooms?.chatRooms.filter(
              (_chatRoom) => _chatRoom.isClosed
            )}
            onSelectChatRoomId={onSelectChatRoomId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
